import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, flow, first, cloneDeep } from 'lodash';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';

import { Matcher, Operator } from 'utils/adl-services';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
} from 'containers/Survey/constants';

import {
  getEmptyAnswer as getEmptyScreeningAnswer,
  getEmptyNoneOfTheAboveAnswer,
} from 'components/ScreeningQuestion/utils';
import {
  getEmptyAnswer,
  noneOfTheAboveAnswerSelector,
} from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
} from '../utils';

const retailCategoryConversionTesting = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourChannel = get(
    find(variables, ['name', 'your_channel']),
    'value',
    {},
  );
  const yourStore = get(find(variables, ['name', 'your_store']), 'value', []);

  const otherProductCategory = get(
    find(variables, ['name', 'other_product_category']),
    'value',
    [],
  );
  const otherStores = get(
    find(variables, ['name', 'other_stores']),
    'value',
    [],
  );

  const period = get(find(variables, ['name', 'period']), 'value', '');
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  // replace general texts
  const replacePeriod = (value) =>
    value.replace(/\{\{period\}\}/gi, escapeHTML(period));
  const replaceProductCategory = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );
  const replaceYourChannel = (value) =>
    value.replace(/\{\{your_channel\}\}/gi, escapeHTML(yourChannel.text));

  const replaceAllTexts = flow([
    replaceYourChannel,
    replaceProductCategory,
    replacePeriod,
  ]);

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceAllTexts,
      htmlPrompt: replaceAllTexts,
      body: replaceAllTexts,
    },
  );

  const {
    nodes: retailCategoryConversionTemplateQuestions,
    screeningQuestions,
  } = mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  const screeningQuestionClone = cloneDeep(screeningQuestions[0]);
  Object.assign(screeningQuestionClone, {
    answers: [
      {
        ...getEmptyScreeningAnswer(),
        body: productCategory.text,
        media: productCategory.media,
      },
      ...otherProductCategory.map((otherCategory) => ({
        ...getEmptyScreeningAnswer(),
        body: otherCategory.text,
        media: otherCategory.media,
        screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
      })),
      {
        ...getEmptyNoneOfTheAboveAnswer(),
        screenerType: SCREENING_ALLOWED_VALUES.STRICT_NOT_ALLOWED,
      },
    ],
  });

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  Survey.targeting.updateScreeningQuestion(screeningQuestionClone);
  applyScreeningQuestionContentUpdate(Survey, screeningQuestionClone, {
    surveyType,
    peeqCategoryType,
  });

  // setup media for questions
  [0, 1, 2, 4].forEach((idx) => {
    Survey.questionnaire.updateQuestion({
      ...retailCategoryConversionTemplateQuestions[idx],
      media: productCategory.media,
    });
  });

  const q3StoreAnswers = yourStore.map((store) => ({
    ...getEmptyAnswer(true),
    body: store.text,
    media: store.media,
  }));
  Survey.questionnaire.updateQuestion({
    ...retailCategoryConversionTemplateQuestions[3],
    media: productCategory.media,
    answers: [
      ...q3StoreAnswers,
      ...otherStores.map((store) => ({
        ...getEmptyAnswer(true),
        body: store.text,
        media: store.media,
      })),
      noneOfTheAboveAnswerSelector(
        retailCategoryConversionTemplateQuestions[3].answers,
      ),
    ],
  });
  [5, 11].forEach((idx) => {
    Survey.questionnaire.updateQuestion({
      ...retailCategoryConversionTemplateQuestions[idx],
      media: yourChannel.media,
    });
  });

  const yourStoreConcat = yourStore.map((store) => store.text).join(', ');
  Survey.questionnaire.updateQuestion({
    ...retailCategoryConversionTemplateQuestions[13],
    prompt: retailCategoryConversionTemplateQuestions[13].prompt.replace(
      /\{\{your_store_1.*your_store_4\}\}/gi,
      escapeHTML(yourStoreConcat),
    ),
    htmlPrompt:
      retailCategoryConversionTemplateQuestions[13].htmlPrompt.replace(
        /\{\{your_store_1.*your_store_4\}\}/gi,
        escapeHTML(yourStoreConcat),
      ),
  });

  Survey.questionnaire.updateQuestion({
    // eslint-disable-next-line no-underscore-dangle
    ...Survey.questionnaire._getQuestion(
      retailCategoryConversionTemplateQuestions[12].refId,
    ),
    media: yourChannel.media,
  });

  // recreate ADL where needed
  // eslint-disable-next-line no-underscore-dangle
  const sourceQuestion = Survey.questionnaire._getQuestion(
    retailCategoryConversionTemplateQuestions[3].refId,
  );
  q3StoreAnswers.forEach((answer, answerIndex) => {
    const questionToAttach = retailCategoryConversionTemplateQuestions[12];
    const questionToGoTo = END_OF_SURVEY;
    const context = { questionId: sourceQuestion.refId };
    if (answerIndex === 0) {
      Survey.questionnaire.linkAnswerToQuestion({
        question: questionToAttach,
        answerRefId: answer.refId,
        targetQuestion: questionToGoTo,
        context,
      });
    } else {
      Survey.questionnaire.addRule(questionToAttach, answer, context);
    }
  });
  Survey.questionnaire.updateRuleOperator(
    retailCategoryConversionTemplateQuestions[12],
    Operator.Any,
  );

  if (shouldConvertScreeningToQlQuestions({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screenerQuestion = screeningQuestionToNormal(screeningQuestionClone);
    Survey.questionnaire.addQuestion(screenerQuestion);

    repointCarryForwardFromScreening(
      Survey,
      screenerQuestion.refId,
      screeningQuestionClone.refId,
    );

    // move to top of questionnaire
    Survey.questionnaire.moveQuestion(
      screenerQuestion,
      // eslint-disable-next-line no-underscore-dangle
      -(Survey.questionnaire._getQuestionCount() - 1),
    );

    Survey.questionnaire.removeAllScreeningQuestions();

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: screenerQuestion,
      answerRefId: first(screenerQuestion.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: screenerQuestion.refId },
      matcher: Matcher.NotEqual,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: screenerQuestion.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });
  } else if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }
  return { state: Survey.state, defaultRecurrence };
};

export default retailCategoryConversionTesting;
